import * as React from 'react';
import type {IEventTemplate} from '../../../types/IEventTemplate';
import type {SequenceItem} from '../../../types/IEventTemplateMetadata';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {calculateDayNumbers} from '../../../utils/sequential';
import {SeparatorWrapper} from '../../helper-components/helper-components';
import {DayCard} from './day-card.tsx';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {SequenceDayItem} from './sequence-day-item';


interface SequenceTimelineProps {
  sequence: SequenceItem[];
  eventTemplate: IEventTemplate;
}

export const SequenceTimeline = ({
  sequence,
  eventTemplate,
}: SequenceTimelineProps) => {
  const {t} = useLabelTranslation();

  if (sequence.length === 1) {
    return (
      <SequenceDayItem
        sequenceDay={sequence[0]}
        eventTemplate={eventTemplate}
      />
    );
  }

  const dayNumberMap = calculateDayNumbers(sequence);

  return (
    <>
      {sequence.map((item, index) => {
        const nextItem = sequence?.[index + 1];
        const shouldShowSeparator =
          nextItem && (item?.type === 'day' || item?.type === 'gap');

        if (item.type === 'day') {
          const dayNumber = dayNumberMap.get(index) || 0;
          return (
            <React.Fragment key={`day-${index}`}>
              <DayCard
                dayNumber={dayNumber}
                sequenceDay={item}
                eventTemplate={eventTemplate}
                index={index}
              />
              {shouldShowSeparator && <SeparatorWrapper />}
            </React.Fragment>
          );
        }
        if (item.type === 'gap') {
          const gapAmount = item.value?.amount || 0;
          const gapUnit = item.value?.unit || t('days', 'days');
          const gapLabel = `${gapAmount} ${gapUnit} ${t('Gap', 'Gap')}`;

          return (
            <React.Fragment key={`gap-${index}`}>
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              <Chip semantic="information">{gapLabel}</Chip>
              {shouldShowSeparator && <SeparatorWrapper />}
            </React.Fragment>
          );
        }

        return null;
      })}
    </>
  );
};
